import '.a2{position:absolute;}.ssr-plugin'
import '.bf{inset:0px;}.ssr-plugin'
import '.g0{top:76px;}.ssr-plugin'
import '.g1{left:284px;}.ssr-plugin'
import '.ai{right:16px;}.ssr-plugin'
import '.g2{height:calc(100vh - 92px);}.ssr-plugin'
import '.bg{overflow:hidden;}.ssr-plugin'
import '.e0{z-index:3;}.ssr-plugin'
import '.g3{top:60px;}.ssr-plugin'
import '.ci{overflow-y:scroll;}.ssr-plugin'
import '.a0{position:relative;}.ssr-plugin'
import '.a{display:flex;}.ssr-plugin'
import '.c{justify-content:center;}.ssr-plugin'
import '.f0{overflow-x:hidden;}.ssr-plugin'
import '.g4{top:14px;}.ssr-plugin'
import '.by{left:0px;}.ssr-plugin'
import '.c0{right:0px;}.ssr-plugin'
import '.a9{pointer-events:none;}.ssr-plugin'
import '.h{width:100%;}.ssr-plugin'
import '.g5{max-width:750px;}.ssr-plugin'
import '.g6{padding:16px 12px;}.ssr-plugin'
import '.x{margin:0 auto;}.ssr-plugin'
import '.g7{max-height:520px;}.ssr-plugin'
import '.g8{min-height:124px;}.ssr-plugin'
import '.bz{top:0px;}.ssr-plugin'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import {
  InitialConfigType,
  LexicalComposer,
} from '@lexical/react/LexicalComposer'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { TablePlugin } from '@lexical/react/LexicalTablePlugin'
import { $generateNodesFromDOM } from '@lexical/html'
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table'
import {
  $createParagraphNode,
  $getRoot,
  isHTMLElement,
  type LexicalEditor,
} from 'lexical'
import { useEffect, useId, useMemo, useRef, useState } from 'react'

import { FloatingLinkPlugin } from './plugins/FloatingLinkPlugin.js'
import { BehaviourPlugin } from './plugins/BehaviourPlugin.js'
import { ValuePlugin } from './plugins/ValuePlugin.js'
import { EmbedPlugin } from './plugins/EmbedPlugin.js'
import { ImagePlugin } from './plugins/ImagePlugin.js'

import { colors } from '../../tokens/colors.js'
import { radius } from '../../utils/radius.js'
import { Text } from '../Text/index.js'
import { Toolbar } from './Toolbar.js'
import { NODES } from './nodes.js'
import { Style, styled } from 'inlines'
import { LabelDescription } from '../LabelDescription/index.js'
import { EDITOR_STYLES } from './EditorStyle.js'
import { MiniNavigation } from './MiniNavigation.js'

type RichTextEditorProps = {
  value?: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  error?: boolean
  style?: Style
  onFocus?: () => void
  onBlur?: () => void
  label?: string
  description?: string
  errorMessage?: string
}

const removeTableCss = (
  editor: LexicalEditor,
  target: TableNode | TableCellNode | TableRowNode,
) => {
  const node = target.exportDOM(editor)
  const { element } = node
  if (isHTMLElement(element)) element.removeAttribute('style')
  return node
}

const htmlExportMap = new Map()
htmlExportMap.set(TableNode, removeTableCss)
htmlExportMap.set(TableRowNode, removeTableCss)
htmlExportMap.set(TableCellNode, removeTableCss)

const BASE_CONFIG: Omit<InitialConfigType, 'namespace' | 'editorState'> = {
  onError: console.error,
  nodes: NODES,
  theme: {
    text: {
      italic: 'italic',
      strikethrough: 'strikethrough',
      underline: 'underline',
      underlineStrikethrough: 'underline-strikethrough',
    },
  },
  html: {
    export: htmlExportMap,
  },
}

let parser: DOMParser
const EMPTY_HTML_RE = /^\s*(<p><br><\/p>|<p><\/p>)\s*$/i
const normalizeHtml = (html: string) => (EMPTY_HTML_RE.test(html) ? '' : html)

const RichTextEditor = ({
  value,
  onChange,
  placeholder,
  disabled,
  style,
  onFocus,
  onBlur,
  label,
  description,
  error,
  errorMessage = 'This field is required',
}: RichTextEditorProps) => {
  const [fullScreen, setFullScreen] = useState(false)

  const id = useId()
  const ref = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const isSmall = (ref.current?.clientWidth ?? 0) < 600

  // Guarda sempre o valor mais recente pra inicializar o editorState no remount
  const valueRef = useRef<string | undefined>(value)
  useEffect(() => {
    valueRef.current = value
  }, [value])

  // ✅ Remonta UMA vez quando value chega pela primeira vez (undefined -> string)
  const [boot, setBoot] = useState(0)
  const hadValueOnce = useRef(false)
  useEffect(() => {
    if (!hadValueOnce.current && value != null) {
      hadValueOnce.current = true
      setBoot((x) => x + 1)
    }
  }, [value])

  const inlineConfig = useMemo<InitialConfigType>(() => {
    return {
      ...BASE_CONFIG,
      namespace: `based_ui_rte_${id}_inline_${boot}`,
      editorState: (editor) => {
        const html = normalizeHtml(valueRef.current ?? '')
        const root = $getRoot()
        root.clear()

        if (html === '') {
          root.append($createParagraphNode())
          return
        }

        parser ??= new DOMParser()
        const dom = parser.parseFromString(html, 'text/html')
        const nodes = $generateNodesFromDOM(editor, dom)
        root.append(...nodes)
      },
    }
  }, [id, boot])

  const fullConfig = useMemo<InitialConfigType>(() => {
    return {
      ...BASE_CONFIG,
      namespace: `based_ui_rte_${id}_full_${boot}`,
      editorState: (editor) => {
        const html = normalizeHtml(valueRef.current ?? '')
        const root = $getRoot()
        root.clear()

        if (html === '') {
          root.append($createParagraphNode())
          return
        }

        parser ??= new DOMParser()
        const dom = parser.parseFromString(html, 'text/html')
        const nodes = $generateNodesFromDOM(editor, dom)
        root.append(...nodes)
      },
    }
  }, [id, boot])

  useEffect(() => {
    if (scrollContainerRef?.current && value) {
      const timeoutId = setTimeout(() => {
        scrollContainerRef.current?.scrollTo({
          top: 0,
          behavior: 'instant' as any,
        })
      }, 0)
      return () => clearTimeout(timeoutId)
    }
  }, [fullScreen])

  const composerKey = `${id}:${boot}:${fullScreen ? 'full' : 'inline'}`

  return fullScreen ? (
    <styled.div className="a2 bf g0 g1 ai g2 bg e0"
      style={{
        background: colors.neutralInverted100,
        border: `1px solid ${colors.neutral20Adjusted}`,
        borderRadius: radius[20],
        ...style,
      }}
    >
      <LexicalComposer key={composerKey} initialConfig={fullConfig}>
        <Toolbar
          fullScreen={fullScreen}
          setFullScreen={setFullScreen}
          isSmall={isSmall}
          disabled={disabled}
          style={{
            position: 'sticky',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 4,
            background: colors.neutralInverted100,
          }}
        />
        <div className="a2 g3 bf ci"
          ref={scrollContainerRef}
        >
          <styled.div className="a0 a c f0"
            style={{
              ...EDITOR_STYLES,
            }}
          >
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  onFocus={onFocus}
                  onBlur={onBlur}
                  className="editor"
                  style={{
                    outline: 'none',
                    padding: isSmall ? '20px 12px' : '32px 12px',
                    width: '100%',
                    maxWidth: 750,
                    margin: '0 auto',
                    pointerEvents: disabled ? 'none' : 'auto',
                  }}
                  tabIndex={disabled ? -1 : undefined}
                />
              }
              placeholder={
                <div className="a2 g4 by c0 a9"
                >
                  <div className="h g5 g6 x"
                  >
                    <Text color="neutral60" variant="display-regular">
                      {placeholder}
                    </Text>
                  </div>
                </div>
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
          </styled.div>

          <BehaviourPlugin />
          <ValuePlugin value={value} onChange={onChange} disabled={disabled} />

          <ListPlugin />
          <LinkPlugin />
          <HistoryPlugin />
          <FloatingLinkPlugin />
          <TablePlugin />
          <EmbedPlugin />
          <ImagePlugin />
        </div>
      </LexicalComposer>

      <MiniNavigation value={value} scrollContainerRef={scrollContainerRef} />
    </styled.div>
  ) : (
    <LabelDescription
      label={label}
      description={description}
      error={error && errorMessage}
    >
      <styled.div className="h bg a0"
        ref={ref}
        tabIndex={disabled ? -1 : undefined}
        style={{
          border: `1px solid ${disabled ? 'transparent' : colors.neutral20Adjusted}`,
          borderRadius: radius[12],
          pointerEvents: disabled ? 'none' : 'auto',
          background: disabled ? colors.neutral5 : 'inherit',
          color: disabled ? colors.neutral20 : 'inherit',
          ...style,
        }}
      >
        <LexicalComposer key={composerKey} initialConfig={inlineConfig}>
          <Toolbar
            fullScreen={fullScreen}
            setFullScreen={setFullScreen}
            isSmall={isSmall}
            disabled={disabled}
          />
          <div className="a0 bf ci g7 g8"
          >
            <styled.div className="a0 a c f0"
              style={{
                ...EDITOR_STYLES,
              }}
            >
              <RichTextPlugin
                contentEditable={
                  <ContentEditable
                    onFocus={onFocus}
                    onBlur={onBlur}
                    className="editor"
                    style={{
                      outline: 'none',
                      padding: isSmall ? '20px 12px' : '20px 24px',
                      width: '100%',
                      maxWidth: 750,
                      margin: '0 auto',
                      pointerEvents: disabled ? 'none' : 'auto',
                    }}
                    tabIndex={disabled ? -1 : undefined}
                  />
                }
                placeholder={
                  <div className="a2 by c0 bz a9"
                  >
                    <div className="h g5 x"
                      style={{
                        padding: isSmall ? '18px 12px' : '18px 24px',}}
                    >
                      <Text color="neutral60" variant="display-regular">
                        {placeholder}
                      </Text>
                    </div>
                  </div>
                }
                ErrorBoundary={LexicalErrorBoundary}
              />
            </styled.div>

            <BehaviourPlugin />
            <ValuePlugin
              value={value}
              onChange={onChange}
              disabled={disabled}
            />

            <ListPlugin />
            <LinkPlugin />
            <HistoryPlugin />
            <FloatingLinkPlugin />
            <TablePlugin />
            <EmbedPlugin />
            <ImagePlugin />
          </div>
        </LexicalComposer>
      </styled.div>
    </LabelDescription>
  )
}

const RichTextEditorStyles = EDITOR_STYLES

export { RichTextEditor, RichTextEditorStyles }
export type { RichTextEditorProps }
